import {
  Component,
  type ReactNode,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { onActivation, onChargement, onPrixClique } from "./actions";
import { EXPORTS, exportPoster } from "./export";
import { FalseDoor, OFFERS, type Offer } from "./fausse-porte";
import { installFonts } from "./fonts";
import {
  assignTiers,
  canvasContext,
  createMeasure,
  layoutPoster,
  type PosterFormat,
} from "./layout";
import { artistLinks, LINK_REL } from "./links";
import { type Band, parseLineup } from "./parse";
import { buildPosterSvg, PosterPreview } from "./poster";
import {
  ACTIVATION_THRESHOLD,
  type AtelierState,
  clampText,
  clampTitle,
  clearState,
  DEFAULT_TITLE,
  loadState,
  MAX_TEXT_LENGTH,
  MAX_TITLE_LENGTH,
  saveState,
} from "./storage";
import { THEME_IDS, THEMES } from "./themes";

/**
 * L'atelier d'affiche, la seule page de l'app (spec, section 1). Le nom du fichier vient
 * du template : Copier recree un fichier du template supprime, donc la page vit ici.
 *
 * Tout se passe dans le navigateur. La page n'envoie rien elle-meme : les seuls appels
 * vers l'exterieur passent par actions.ts (prix cliques, activation, liste d'attente),
 * branche par le ticket 4, et la liste de groupes n'y entre jamais, sauf son nombre.
 */

export const HOOK_LINE = "Tous les groupes que tu as vus en concert, sur une seule affiche.";

// Affiche d'exemple tant que la liste est vide : des phrases, pas de vrais groupes.
export const EXAMPLE_BANDS: Band[] = [
  "Le groupe vu trois fois",
  "Ton premier concert",
  "Celui du festival sous la pluie",
  "La première partie qui t'a scotché",
  "Le groupe du copain",
  "Un soir de semaine",
  "Le concert gratuit",
  "Celui que tu racontes encore",
  "Le rappel de trop",
  "La salle trop petite",
  "Le groupe de tes quinze ans",
  "Une découverte par hasard",
].map((name, index) => ({ key: `exemple-${index}`, name, count: index === 0 ? 3 : 1 }));

function Atelier() {
  const [state, setState] = useState<AtelierState>(loadState);
  const [fontsReady, setFontsReady] = useState(false);
  const [busy, setBusy] = useState<PosterFormat | null>(null);
  const [exportError, setExportError] = useState("");
  const [offer, setOffer] = useState<Offer | null>(null);

  const deferredText = useDeferredValue(state.text);
  const parsed = useMemo(() => parseLineup(deferredText), [deferredText]);
  const bands = parsed.bands;
  const count = bands.length;
  const starred = useMemo(() => new Set(state.starred), [state.starred]);
  const theme = THEMES[state.theme];

  useEffect(() => {
    let alive = true;
    installFonts().then(() => {
      if (alive) {
        setFontsReady(true);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    onChargement();
  }, []);

  useEffect(() => {
    saveState(state);
  }, [state]);

  useEffect(() => {
    // La page ne retient rien : l'unicite par navigateur est l'affaire de actions.ts.
    if (count >= ACTIVATION_THRESHOLD) {
      onActivation(count);
    }
  }, [count]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: les largeurs changent quand les polices arrivent.
  const measure = useMemo(() => createMeasure(canvasContext()), [fontsReady]);

  const buildSvg = useCallback(
    (format: PosterFormat) => {
      const entries = assignTiers(count > 0 ? bands : EXAMPLE_BANDS, starred);
      const layout = layoutPoster(
        { entries, title: state.title.trim() || DEFAULT_TITLE, theme, format },
        measure,
      );
      return buildPosterSvg({ layout, theme });
    },
    [bands, count, starred, state.title, theme, measure],
  );

  const previewSvg = useMemo(() => buildSvg("post"), [buildSvg]);

  const update = (patch: Partial<AtelierState>) =>
    setState((current) => ({ ...current, ...patch }));

  const toggleStar = (key: string) =>
    setState((current) => ({
      ...current,
      starred: current.starred.includes(key)
        ? current.starred.filter((k) => k !== key)
        : [...current.starred, key],
    }));

  async function download(format: PosterFormat) {
    setBusy(format);
    setExportError("");
    try {
      await installFonts();
      await exportPoster(buildSvg(format), EXPORTS[format]);
    } catch {
      setExportError("L'image n'a pas pu être créée sur ce navigateur.");
    } finally {
      setBusy(null);
    }
  }

  function choose(selected: Offer) {
    onPrixClique(selected.id);
    setOffer(selected);
  }

  const closeOffer = useCallback(() => setOffer(null), []);

  const shownTitle = state.title.trim() || DEFAULT_TITLE;
  const posterLabel =
    count > 0
      ? `Aperçu de l'affiche « ${shownTitle} », ${count} groupe${count > 1 ? "s" : ""}`
      : "Exemple d'affiche, en attendant tes groupes";

  return (
    <div className="relative left-1/2 w-[min(72rem,calc(100vw-2rem))] -translate-x-1/2">
      <header className="mb-6 max-w-2xl">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Le line-up de ta vie</h1>
        <p className="mt-2 text-lg text-muted-foreground">{HOOK_LINE}</p>
      </header>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
        <section
          aria-label="Tes groupes"
          className="flex flex-col gap-4 lg:col-start-1 lg:row-start-1"
        >
          <label className="flex flex-col gap-2">
            <span className="font-medium">Colle ou tape tes groupes, un par ligne</span>
            <textarea
              className="min-h-48 rounded-md border border-input bg-background p-3 text-base leading-relaxed lg:min-h-72"
              maxLength={MAX_TEXT_LENGTH}
              onChange={(event) => update({ text: clampText(event.target.value) })}
              placeholder={"Tryo\nMetallica\nNoir Désir\n..."}
              spellCheck={false}
              value={state.text}
            />
          </label>
          <p aria-live="polite" className="text-sm text-muted-foreground" data-testid="compteur">
            {count === 0
              ? "Aucun groupe pour l'instant."
              : `${count} groupe${count > 1 ? "s" : ""}`}
            {parsed.truncated ? " (300 au plus : les suivants sont ignorés)" : ""}
          </p>

          <label className="flex flex-col gap-2">
            <span className="font-medium">Titre de l'affiche</span>
            <input
              className="h-11 rounded-md border border-input bg-background px-3 text-base"
              maxLength={MAX_TITLE_LENGTH}
              onChange={(event) => update({ title: clampTitle(event.target.value) })}
              value={state.title}
            />
          </label>

          <fieldset>
            <legend className="mb-2 font-medium">Thème</legend>
            <div className="grid grid-cols-3 gap-2">
              {THEME_IDS.map((id) => (
                <button
                  aria-pressed={state.theme === id}
                  className="h-11 rounded-md border border-input px-3 font-medium aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground"
                  key={id}
                  onClick={() => update({ theme: id })}
                  type="button"
                >
                  {THEMES[id].label}
                </button>
              ))}
            </div>
          </fieldset>
        </section>

        <section
          aria-label="Ton affiche"
          className="flex flex-col gap-4 lg:sticky lg:top-4 lg:col-start-2 lg:row-span-3 lg:row-start-1"
        >
          <PosterPreview label={posterLabel} svg={previewSvg} />
          <div className="grid grid-cols-2 gap-2">
            <button
              className="h-12 rounded-md bg-primary px-3 font-semibold text-primary-foreground disabled:opacity-50"
              disabled={busy !== null || count === 0}
              onClick={() => download("post")}
              type="button"
            >
              {busy === "post" ? "Création..." : "Télécharger l'image"}
            </button>
            <button
              className="h-12 rounded-md border border-input px-3 font-semibold disabled:opacity-50"
              disabled={busy !== null || count === 0}
              onClick={() => download("story")}
              type="button"
            >
              {busy === "story" ? "Création..." : "Format story"}
            </button>
          </div>
          {exportError ? (
            <p className="text-sm text-destructive" role="alert">
              {exportError}
            </p>
          ) : null}
          <p className="text-sm text-muted-foreground">
            Gratuit, sans compte. Ta liste reste dans ton navigateur.
          </p>
        </section>

        <section aria-label="Offres" className="lg:col-start-1 lg:row-start-2">
          <div className="rounded-lg border border-border p-4">
            <h2 className="font-semibold">Pour aller plus loin</h2>
            <ul className="mt-3 flex flex-col gap-2">
              {OFFERS.map((item) => (
                <li key={item.id}>
                  <button
                    className="flex w-full items-center justify-between gap-3 rounded-md border border-input px-3 py-2 text-left hover:bg-muted"
                    data-product={item.id}
                    onClick={() => choose(item)}
                    type="button"
                  >
                    <span className="flex flex-col">
                      <span className="font-medium">{item.name}</span>
                      <span className="text-sm text-muted-foreground">{item.detail}</span>
                    </span>
                    <span className="shrink-0 font-semibold">{item.price}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section aria-labelledby="ma-liste" className="lg:col-start-1 lg:row-start-3">
          <h2 className="text-xl font-semibold" id="ma-liste">
            Ma liste
          </h2>
          {count === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">
              Tes groupes apparaîtront ici, avec de quoi les réécouter.
            </p>
          ) : (
            <>
              <p className="mt-1 text-sm text-muted-foreground">
                L'étoile passe un groupe en tête d'affiche. Un groupe écrit deux fois y monte tout
                seul.
              </p>
              <ul className="mt-3 divide-y divide-border">
                {bands.map((band) => {
                  const isStarred = starred.has(band.key);
                  return (
                    <li className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2" key={band.key}>
                      <button
                        aria-label={`Mettre ${band.name} en tête d'affiche`}
                        aria-pressed={isStarred}
                        className="flex h-10 w-10 items-center justify-center rounded-md text-xl leading-none text-muted-foreground aria-pressed:text-primary"
                        onClick={() => toggleStar(band.key)}
                        type="button"
                      >
                        {isStarred ? "\u{2605}" : "\u{2606}"}
                      </button>
                      <span className="min-w-0 flex-1 basis-[calc(100%-3rem)] break-words font-medium sm:basis-0">
                        {band.name}
                        {band.count > 1 ? (
                          <span className="ml-2 whitespace-nowrap text-sm font-normal text-muted-foreground">
                            vu {band.count} fois
                          </span>
                        ) : null}
                      </span>
                      <span className="ml-12 flex gap-1 text-sm sm:ml-auto">
                        {artistLinks(band.name).map((link) => (
                          <a
                            aria-label={`${band.name} sur ${link.label}`}
                            className="rounded-md border border-input px-2 py-1 hover:bg-muted"
                            href={link.href}
                            key={link.id}
                            rel={LINK_REL}
                            target="_blank"
                          >
                            {link.label}
                          </a>
                        ))}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>
      </div>

      {offer ? <FalseDoor offer={offer} onClose={closeOffer} /> : null}
    </div>
  );
}

interface BoundaryState {
  failed: boolean;
}

/**
 * Filet de securite : si l'atelier plante (un texte que rien n'a prevu), la page propose
 * d'effacer la liste gardee au lieu de rester blanche, rechargement compris.
 */
export class AtelierBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  override state: BoundaryState = { failed: false };

  static getDerivedStateFromError(): BoundaryState {
    return { failed: true };
  }

  reset = () => {
    clearState();
    this.setState({ failed: false });
  };

  override render() {
    if (!this.state.failed) {
      return this.props.children;
    }
    return (
      <section className="flex flex-col items-start gap-4" role="alert">
        <h1 className="text-2xl font-semibold">L'affiche n'a pas pu se dessiner.</h1>
        <p className="text-muted-foreground">
          Ta liste contient sans doute un caractère que la page ne sait pas lire. Tu peux l'effacer
          et la recoller.
        </p>
        <button
          className="h-11 rounded-md bg-primary px-4 font-medium text-primary-foreground"
          onClick={this.reset}
          type="button"
        >
          Effacer ma liste
        </button>
      </section>
    );
  }
}

/** L'atelier, sous son filet de securite. */
export function HomePage() {
  return (
    <AtelierBoundary>
      <Atelier />
    </AtelierBoundary>
  );
}
